<template>
  <main class="account" :class="{ 'account--hide-image': hideImage }">
    <section class="account__column account__column--cta">
      <div v-if="!resetSuccess">
        <header class="account__header">
          <h1 class="account__title">
            {{
              isPasswordExpired
                ? $t('common.password_expired')
                : $t('pages.passwordReset.header')
            }}
          </h1>
          <div v-if="!isLoading">
            <p v-if="!isValidToken" class="account__subtitle">
              {{ $t('pages.passwordReset.textInvalidToken') }}
            </p>
            <p
              v-if="isPasswordExpired && isValidToken"
              class="account__subtitle"
              data-cy="password-expired-desc"
            >
              {{ $t('pages.passwordExpired.description') }}
            </p>
            <p
              v-if="
                isPasswordExpired &&
                isValidToken &&
                parseInt(passwordExpiryTime, 10) > 0
              "
              class="account__subtitle u-margin-top-4x u-color-primary-text"
              data-cy="password-expired-time"
            >
              <strong>{{
                $t('pages.passwordExpired.expiryTime', { passwordExpiryTime })
              }}</strong>
            </p>
          </div>
        </header>

        <BaseSpinner v-show="isLoading" class="u-margin-top-12x"></BaseSpinner>

        <form
          v-if="isValidToken && !isLoading"
          data-cy="password-reset-form"
          @submit.prevent="resetPassword(userId, token, password)"
        >
          <PasswordInput
            id="new-password"
            v-model="password"
            class="u-margin-bottom-12x"
            :label="$t('pages.passwordReset.textResetPassword')"
            :show-errors="showErrors"
            :is-valid.sync="isPasswordValid"
            :error="error"
            data-cy="passing-password-input"
          >
          </PasswordInput>
          <BaseButton
            data-cy="reset-password-submit"
            class="u-margin-bottom-4x"
            :loading="isLoadingButton"
            type="submit"
          >
            {{
              isPasswordExpired
                ? $t('common.btnCreate')
                : $t('pages.passwordReset.buttonResetPassword')
            }}
          </BaseButton>
        </form>

        <BaseButton to="/login" variant="link">
          {{ $t('common.linkBackToLogin') }}
        </BaseButton>
      </div>

      <div v-else>
        <header class="account__header">
          <h1 class="account__title">
            {{ $t('pages.passwordReset.passwordResetDone') }}
          </h1>
        </header>
        <BaseAlert
          class="u-margin-bottom-4x"
          :title="$t('common.success')"
          data-cy="password-reset-success"
          variant="success"
          :value="true"
        >
          <p>
            {{ $t('pages.passwordReset.alertPasswordResetSuccess') }}
          </p>
        </BaseAlert>
        <BaseButton
          to="/login"
          data-cy="back-to-login"
          :loading="isLoadingButton"
        >
          {{ $t('pages.passwordReset.backToLogin') }}
        </BaseButton>
      </div>
    </section>

    <section class="account__column account__column--showcase">
      <img src="/img/reset-pass.svg" class="account__img" />
    </section>
  </main>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import ResetPasswordService from '@/common/services/ResetPassword';
import { whitelabel } from '@te/cdev-utils';
import {
  BaseAlert,
  PasswordInput,
  BaseButton,
  BaseSpinner
} from '@te/cdev-design-system';

export default defineComponent({
  metaInfo() {
    return {
      title: this.$t('pages.passwordReset.title')
    };
  },
  components: {
    PasswordInput,
    BaseAlert,
    BaseButton,
    BaseSpinner
  },
  props: {
    isPasswordExpired: {
      type: Boolean,
      default: false
    },
    token: {
      type: String,
      default: ''
    },
    userId: {
      type: String,
      default: ''
    },
    passwordExpiryTime: {
      type: String,
      default: '0'
    }
  },
  data() {
    return {
      isLoading: true,
      isLoadingButton: false,
      isValidToken: false,
      inputType: 'password',
      password: '',
      passwordRepeat: '',
      hideImage: whitelabel('hideLoginImage'),
      showErrors: false,
      isPasswordValid: false,
      error: '',
      resetSuccess: false
    };
  },
  computed: {
    matchPassword(): boolean {
      return this.password === this.passwordRepeat;
    }
  },
  mounted() {
    this.isValidResetToken(this.userId, this.token);
  },
  methods: {
    async isValidResetToken(userId: string, token: string) {
      try {
        await ResetPasswordService.isValidResetToken(userId, token);
        this.isValidToken = true;
      } catch {
        this.isValidToken = false;
      }
      this.isLoading = false;
    },

    async resetPassword(userId: string, token: string, password: string) {
      this.error = '';
      this.showErrors = false;
      if (!this.isPasswordValid) {
        this.showErrors = true;
        return;
      }
      this.isLoadingButton = true;

      try {
        await ResetPasswordService.resetPassword(userId, token, password);
        this.resetSuccess = true;
      } catch (response) {
        if (response instanceof Response) {
          const error = await response.json();
          if (
            error.code === 'VALIDATION_FAIL' &&
            error.errors.password.includes('PASSWORD_UNCHANGED')
          ) {
            this.error = this.$t('error.PASSWORD_UNCHANGED');
          }
        }
      }
      this.isLoadingButton = false;
    }
  }
});
</script>

<style lang="scss">
.account__header {
  margin: 0;
  padding: 3.125rem 0;
}

.account__title {
  padding: 0;
  margin-bottom: 0;
}

.account__subtitle {
  @include font-size(md);
  margin-top: 0.5em;
  margin-bottom: 0;
}
</style>
